import { useRef, useState } from 'react';
import {
  Grid,
  GridCellProps,
  GridColumn as Column,
  GridDataStateChangeEvent,
  GridNoRecords
} from '@progress/kendo-react-grid';
import { State, toDataSourceRequestString } from '@progress/kendo-data-query';
import { ColumnMenu } from '../components/columnMenu';
import { ExcelExport } from '@progress/kendo-react-excel-export';
import Toolbar from '../components/page-toolbar';
import {
  GridToolBar,
  GridToolbarDataStateChangeEvent
} from '../components/grid/grid-tool-bar';
import { DefaultGridSettings } from '../constants/grid-settings';
import { NoRecords } from '../components/grid/no-records';
import { formatDateToDDMMYYYY } from '../utils/date';
import { useGetExternalEvents } from '../hooks/services/use-external-events';
import { Loader } from '@progress/kendo-react-indicators';

const initialDataState: State = {
  sort: [{ field: 'eventDate', dir: 'desc' }],
  ...DefaultGridSettings.initialDataState
};

const GlobalEventsManagementGrid = () => {
  const [dataState, setDataState] = useState<State>(initialDataState);
  const [gridMode, setGridMode] = useState<string>('grid');
  const gridExportRef = useRef<ExcelExport | null>(null);

  const { eventsData, isLoading } = useGetExternalEvents(
    toDataSourceRequestString(dataState)
  );

  const DateCell = (props: GridCellProps) => {
    return (
      <td className={'k-table-td'}>
        {formatDateToDDMMYYYY(props.dataItem.eventDate, false)}
      </td>
    );
  };

  const handleDataStateChange = (
    e: GridDataStateChangeEvent | GridToolbarDataStateChangeEvent
  ) => {
    const newState = {
      ...e.dataState,
      filter: {
        logic: 'and',
        filters: [e.dataState.filter!]
      }
    } as State;
    setDataState(newState);
  };

  return (
    <>
      <Toolbar title="Catalyst Global Events" />
      <div
        className="pt-2"
        style={{
          flexGrow: 1,
          display: 'flex',
          flexDirection: 'column',
          minHeight: 0,
          width: '100%',
          overflow: 'hidden'
        }}>
        <GridToolBar
          showCardMode={false}
          searchPlaceholder="Search global events"
          columnsToSearch={[
            'organisationName',
            'userName',
            'productTypeName',
            'eventIdentifier'
          ]}
          exportRef={gridExportRef}
          onGridModeChange={(e) => setGridMode(e.gridMode)}
          {...dataState}
          onDataStateChange={handleDataStateChange}
        />
        <ExcelExport
          data={eventsData.data}
          ref={gridExportRef}
          fileName="global-events.xlsx">
          {gridMode === 'grid' && (
            <Grid
              id="grid-global-events"
              style={{
                flexGrow: 1,
                minHeight: 0,
                height: '100%',
                overflow: 'hidden'
              }}
              pageable={DefaultGridSettings.pagerSettings}
              sortable
              data={eventsData}
              {...dataState}
              onDataStateChange={(e: GridDataStateChangeEvent) => {
                setDataState(e.dataState);
              }}>
              <Column
                field="organisationName"
                title="Organisation"
                columnMenu={ColumnMenu}
              />
              <Column field="userName" title="User" columnMenu={ColumnMenu} />
              <Column
                field="productTypeName"
                title="Product Type"
                columnMenu={ColumnMenu}
              />
              <Column
                field="eventDate"
                title="Event Date"
                cells={{ data: DateCell }}
              />
              <Column field="pax" title="Pax" columnMenu={ColumnMenu} />
              <Column
                field="eventIdentifier"
                title="Event Identifier"
                columnMenu={ColumnMenu}
              />

              <GridNoRecords>
                {isLoading ? (
                  <div
                    className={
                      'd-flex flex-column align-items-center gap-2 text-muted'
                    }>
                    <Loader type={'pulsing'} />
                    <span>Loading data</span>
                  </div>
                ) : (
                  <NoRecords />
                )}
              </GridNoRecords>
            </Grid>
          )}
        </ExcelExport>
      </div>
    </>
  );
};

export default GlobalEventsManagementGrid;
